import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FiHome,
  FiCalendar,
  FiUsers,
  FiTrendingUp,
  FiUser,
  FiLogOut,
  FiX,
  FiSettings,
  FiClock,
} from 'react-icons/fi';
import { FaRobot, FaTrophy, FaCalculator, FaClipboardList, FaBell, FaCog, FaComment, FaBook, FaGavel, FaHeartbeat } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  const { logout } = useAuth();

  // Stranded-match count for the Scoring Health badge. Refreshed every 5 min.
  const [healthCount, setHealthCount] = useState(0);
  useEffect(() => {
    let active = true;
    const load = () =>
      apiService
        .getFinalizationAudit(14, 2)
        .then((d) => {
          if (active) setHealthCount(d?.problemCount || 0);
        })
        .catch(() => {
          /* badge is best-effort; ignore errors */
        });
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);

  const menuItems: { id: string; label: string; icon: any; path: string; badge?: number }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: FiHome, path: '/dashboard' },
    { id: 'series', label: 'Series', icon: FiCalendar, path: '/dashboard/series' },
    { id: 'matches', label: 'Matches', icon: FaTrophy, path: '/dashboard/matches' },
    { id: 'contests', label: 'Contests', icon: FiTrendingUp, path: '/dashboard/contests' },
    { id: 'auctions', label: 'Auctions', icon: FaGavel, path: '/dashboard/auctions' },
    { id: 'tracker', label: 'Tracker', icon: FaRobot, path: '/dashboard/tracker' },
    { id: 'scheduler-jobs', label: 'Scheduler Jobs', icon: FiClock, path: '/dashboard/scheduler-jobs' },
    { id: 'scoring-health', label: 'Scoring Health', icon: FaHeartbeat, path: '/dashboard/scoring-health', badge: healthCount },
    { id: 'users', label: 'Users', icon: FiUsers, path: '/dashboard/users' },
    { id: 'plan-rules', label: 'Plan Config', icon: FiSettings, path: '/dashboard/plan-rules' },
    { id: 'scoring-rules', label: 'Scoring System Config', icon: FaCalculator, path: '/dashboard/scoring-rules' },
    { id: 'contest-rules', label: 'Rules Config', icon: FaClipboardList, path: '/dashboard/contest-rules' },
    { id: 'notifications', label: 'Notifications', icon: FaBell, path: '/dashboard/notifications' },
    { id: 'feedback', label: 'Feedback', icon: FaComment, path: '/dashboard/feedback' },
    { id: 'app-settings', label: 'App Settings', icon: FaCog, path: '/dashboard/app-settings' },
    { id: 'system-docs', label: 'System Docs', icon: FaBook, path: '/dashboard/system-docs' },
    { id: 'profile', label: 'Profile', icon: FiUser, path: '/dashboard/profile' },
  ];

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-gray-900 dark:bg-dark-900 bg-opacity-50 dark:bg-opacity-70 z-40 transition-opacity backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gradient-to-b dark:from-dark-500 dark:to-dark-600 border-r border-gray-200 dark:border-dark-400/30 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-dark-400/30">
            <div className="flex items-center">
              <img
                src={require('../assets/images/logo.jpg')}
                alt="CricClash Logo"
                className="h-8 w-auto object-contain rounded-full ring-2 ring-blue-500/20 dark:ring-primary-500/20"
              />
              <span className="ml-3 text-xl font-bold text-gray-900 dark:bg-gradient-to-r dark:from-primary-400 dark:to-accent-cyan dark:bg-clip-text dark:text-transparent">CricClash</span>
            </div>
            {/* Close button for mobile */}
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-400/30"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-3">
              Main Menu
            </div>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 ${
                      isActive
                        ? 'bg-blue-50 dark:bg-gradient-to-r dark:from-primary-600 dark:to-primary-700 text-blue-700 dark:text-white shadow-sm dark:shadow-lg dark:shadow-primary-900/50'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-400/30 hover:text-gray-900 dark:hover:text-white'
                    }`
                  }
                  end={item.path === '/dashboard'}
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-600 dark:text-accent-cyan' : 'text-gray-400'}`} />
                      <span className="flex-1">{item.label}</span>
                      {item.badge ? (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold">
                          {item.badge}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-3 border-t border-gray-200 dark:border-dark-400/30">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-danger-500/10 hover:text-red-600 dark:hover:text-danger-500 rounded-lg transition-all duration-150"
            >
              <FiLogOut className="mr-3 h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
